import React, { useState, useEffect } from 'react';
import {
  TabSection,
  SectionTitle,
  SectionDescription,
  UserList,
  UserCard,
  UserInfo,
  UserDetails,
  UserName,
  UserEmail,
  RoleSelect,
  ActionButton,
  LoadingSpinner
} from '../../../pages/admin/components/styled';
import { 
  getAllUsers, 
  getAllCompanies, 
  FirestoreUser
} from '../../../services/creditService';
import { updateUserRole, updateUserStatus } from '../../../services/userService';

interface GlobalUser extends Omit<FirestoreUser, 'lastLogin'> {
  companyDomain: string;
  companyName: string;
  isActive: boolean;
  lastLogin?: Date;
  creditsUsed?: number;
}

interface Props {
  companyFilter?: string | null;
}

// Move static functions outside component to avoid recreation on each render
const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin': return '#ef4444';
    case 'super_admin': return '#f59e0b';
    case 'user': return '#6366f1';
    default: return '#64748b';
  }
};

const GlobalUsersTab = ({ companyFilter }: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<GlobalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);

  // Load real users from Firestore
  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        // Get all users from Firestore
        const firestoreUsers = await getAllUsers();
        
        // Get all companies to map company IDs to domains and names
        const companies = await getAllCompanies();
        const companyMap = new Map(companies.map(c => [c.id, { domain: c.domain, name: c.name }]));

        // Transform Firestore users to GlobalUser format
        const globalUsers: GlobalUser[] = firestoreUsers.map(user => {
          const company = companyMap.get(user.companyId);
          return {
            ...user,
            companyDomain: company?.domain || 'unknown.com',
            companyName: company?.name || 'Unknown Company',
            isActive: user.status !== 'inactive',
            lastLogin: user.lastLogin ? new Date(user.lastLogin) : undefined,
            creditsUsed: 0
          };
        });

        setUsers(globalUsers);
      } catch (error) {
        console.error('Failed to load users:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  const filteredUsers = users.filter(user => {
    // Filter by search term
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.companyName.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by company if companyFilter is provided
    const matchesCompany = !companyFilter || user.companyDomain.includes(companyFilter);
    
    return matchesSearch && matchesCompany;
  });

  const handleRoleChange = async (userId: string, newRole: string) => {
    setUpdatingUser(userId);
    try {
      await updateUserRole(userId, newRole as 'user' | 'admin' | 'super_admin');
      
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, role: newRole as 'user' | 'admin' | 'super_admin' } : user
        )
      );
    } catch (error) {
      console.error('Failed to update user role:', error);
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleToggleStatus = async (userId: string) => {
    setUpdatingUser(userId);
    try {
      const currentUser = users.find(user => user.id === userId);
      const newStatus = currentUser?.isActive ? 'inactive' : 'active';
      
      await updateUserStatus(userId, newStatus);
      
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, isActive: newStatus === 'active', status: newStatus } : user
        )
      );
    } catch (error) {
      console.error('Failed to toggle user status:', error);
    } finally {
      setUpdatingUser(null);
    }
  };

  return (
    <TabSection>
      <SectionTitle>
        {companyFilter ? '👥 Company Users' : '🌍 Global Users'}
      </SectionTitle>
      <SectionDescription>
        {companyFilter 
          ? `Managing users for company ID: ${companyFilter}` 
          : 'Manage all users across the platform.'
        }
      </SectionDescription>

      {loading ? (
        <LoadingSpinner>Loading users...</LoadingSpinner>
      ) : (
        <>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px', 
            marginBottom: '24px',
            flexWrap: 'wrap'
          }}>
        <input
          type="text"
          placeholder="Search users by name, email, or company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '8px',
            padding: '12px 16px',
            color: '#f8fafc',
            fontSize: '0.9rem',
            flex: 1,
            maxWidth: '400px'
          }}
        />
        <div style={{ color: '#cbd5e1' }}>
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>

      <UserList>
        {filteredUsers.map((user) => (
          <UserCard key={user.id} $isActive={user.isActive}>
            <UserInfo>
              <UserDetails>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <UserName>{user.name}</UserName>
                  <div style={{
                    background: getRoleColor(user.role),
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    textTransform: 'uppercase'
                  }}>
                    {user.role}
                  </div>
                  <div style={{
                    background: user.isActive ? '#22c55e' : '#64748b',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>
                <UserEmail>{user.email}</UserEmail>
                <div style={{ 
                  color: '#cbd5e1', 
                  fontSize: '0.8rem', 
                  marginTop: '4px',
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  <span>🏢 {user.companyName}</span>
                  <span>💳 {user.creditsUsed} credits used</span>
                  <span>🕒 {user.lastLogin ? `Last: ${user.lastLogin.toLocaleDateString()}` : 'Never logged in'}</span>
                </div>
              </UserDetails>
            </UserInfo>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <RoleSelect
                value={user.role}
                onChange={(e) => handleRoleChange(user.id, e.target.value)}
                disabled={updatingUser === user.id}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </RoleSelect>
              
              <ActionButton
                variant={user.isActive ? "secondary" : "primary"}
                onClick={() => handleToggleStatus(user.id)}
                disabled={updatingUser === user.id}
              >
                {updatingUser === user.id ? 'Updating...' : (user.isActive ? 'Deactivate' : 'Activate')}
              </ActionButton>
            </div>
          </UserCard>
        ))}
      </UserList>

      {filteredUsers.length === 0 && searchTerm && (
        <div style={{
          textAlign: 'center',
          padding: '48px 24px',
          color: '#94a3b8',
          fontSize: '0.9rem'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '16px' }}>🔍</div>
          <div style={{ marginBottom: '8px', fontWeight: '500' }}>No users found</div>
          <div>Try adjusting your search terms or clear the filter</div>
        </div>        )}
        </>
      )}
    </TabSection>
  );
};

export default GlobalUsersTab;
